import React from "react";
import {
  PatientInfoCardWrapper,
  DemographicsSection,
  ProgressGaugeSection,
  GaugeContainer,
  RiskGroupContainer,
  MobileBottomInfoRow,
} from "../PatientPortal.styles";

export default function PatientPortalInfoCard({ paciente, edad, grupos, porcentaje }) {
  const formatAge = () => {
    if (!edad) return "";
    const { anios, meses, dias } = edad;
    if (anios === 0 && meses < 1) {
      return ` ${dias} ${dias === 1 ? "Día" : "Días"}`;
    } else if (anios === 0) {
      return ` ${meses} ${meses === 1 ? "Mes" : "Meses"}`;
    } else if (anios <= 2) {
      return ` ${anios} ${anios === 1 ? "Año" : "Años"} ${meses} ${meses === 1 ? "Mes" : "Meses"}`;
    } else {
      return ` ${anios} ${anios === 1 ? "Años" : "Años"}`;
    }
  };

  // Deduplicar grupos activos
  const gruposRaw = Array.isArray(grupos) ? grupos : [];
  const gruposNombresUnicos = [];
  const gruposActivos = [];

  gruposRaw.forEach((g) => {
    const nombre = g.grupo || g.nombre || g.descripcion || "";
    if (nombre && !gruposNombresUnicos.includes(nombre)) {
      gruposNombresUnicos.push(nombre);
      gruposActivos.push(g);
    }
  });

  const nombrePrimerGrupo =
    gruposActivos.length > 0
      ? gruposActivos[0].grupo || gruposActivos[0].nombre || gruposActivos[0].descripcion || "Joven"
      : "Joven";

  const pct = Math.min(100, Math.max(0, Math.round(Number(porcentaje) || 0)));
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (pct / 100) * circumference;

  return (
    <PatientInfoCardWrapper>
      {/* 1. Datos Demográficos */}
      <DemographicsSection>
        {/* Cabecera exclusiva de Mobile (Avatar circular + Nombre) */}
        <div className="patient-header-mobile">
          <div className="mobile-avatar-frame">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0284c7"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="mobile-patient-name">
            {paciente?.fullName || "PACIENTE"}
          </div>
        </div>

        {/* Nombre en Vista Desktop / Tablet */}
        <h2 className="patient-name desktop-only-name">
          {paciente?.fullName || "PACIENTE"}
        </h2>

        <p className="demographic-row">
          <strong>Fecha de Nto:</strong> {paciente?.Fecha_Nto || "No registra"}
        </p>
        <p className="demographic-row">
          <strong>Edad:</strong> {formatAge()} | <strong>Sexo:</strong>{" "}
          {paciente?.Sexo === "M" ? "Masculino" : "Femenino"}
        </p>
        <p className="demographic-row">
          <strong>EAPB:</strong> {paciente?.Entidad || "No registra"}
        </p>
      </DemographicsSection>

      {/* 2. Anillo de Progreso Circular con Icono Avatar (Desktop / Tablet) */}
      <ProgressGaugeSection>
        <GaugeContainer>
          <svg className="gauge-svg" viewBox="0 0 100 100">
            {/* Círculo de fondo */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="6"
            />
            {/* Arco de progreso */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#244B7A"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: "stroke-dashoffset 0.6s ease-in-out",
              }}
            />
          </svg>

          <div className="gauge-inner-content">
            <span className="gauge-percentage">{pct}%</span>
            <div className="gauge-avatar-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="7" r="3.5" stroke="#475569" strokeWidth="1.5" fill="none" />
                <path
                  d="M6 19C6 15.5 8.7 13.5 12 13.5C15.3 13.5 18 15.5 18 19"
                  stroke="#475569"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  fill="none"
                />
                <path
                  d="M8.5 14.5V17C8.5 18.2 9.8 19 11 19H13C14.2 19 15.5 18.2 15.5 17V14.5"
                  stroke="#475569"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  fill="none"
                />
                <circle cx="13" cy="20" r="1" fill="#475569" />
              </svg>
            </div>
          </div>
        </GaugeContainer>
      </ProgressGaugeSection>

      {/* 3. Grupos de Riesgo (Desktop / Tablet) */}
      <RiskGroupContainer>
        <div className="risk-header">Grupos de Riesgo:</div>
        <div className="risk-items-list">
          {gruposActivos.length === 0 ? (
            <div className="risk-item">
              <span className="bullet">•</span>
              <span>Joven</span>
            </div>
          ) : (
            gruposActivos.map((grupo, idx) => {
              const nombre =
                grupo.grupo || grupo.nombre || grupo.descripcion || "Joven";
              return (
                <div className="risk-item" key={grupo.id || `risk-${idx}`}>
                  <span className="bullet">•</span>
                  <span>{nombre}</span>
                </div>
              );
            })
          )}
        </div>
      </RiskGroupContainer>

      {/* 4. Fila Inferior Adaptativa para Mobile (Gauge + Cumplimiento | Pastilla de Riesgo) */}
      <MobileBottomInfoRow>
        <div className="mobile-gauge-group">
          <div className="gauge-mini-box">
            <svg viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke="#E2E8F0"
                strokeWidth="8"
              />
              <circle
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke="#244B7A"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={238.76}
                strokeDashoffset={238.76 - (pct / 100) * 238.76}
              />
            </svg>
            <span className="mini-pct">{pct}%</span>
          </div>
          <span className="cumplimiento-label">Cumplimiento</span>
        </div>

        <div className="mobile-risk-pill">
          Riesgo: {nombrePrimerGrupo}
        </div>
      </MobileBottomInfoRow>
    </PatientInfoCardWrapper>
  );
}
